import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, InputAdornment, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { opsApi } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { PlanSettings } from '../../types';
import { errorMessage, fieldErrors } from '../../utils/errors';
import { formatDateTime } from '../../utils/format';

type Form = Record<'pro_price_monthly' | 'pro_price_yearly' | 'pro_tailored_per_month' | 'trial_days' | 'trial_tailored', string>;

const formOf = (settings: PlanSettings): Form => ({
  pro_price_monthly: settings.pro_price_monthly ?? '',
  pro_price_yearly: settings.pro_price_yearly ?? '',
  pro_tailored_per_month: String(settings.pro_tailored_per_month),
  trial_days: String(settings.trial_days),
  trial_tailored: String(settings.trial_tailored),
});

/**
 * The Plans section of the staff area: what Pro costs a month and a year,
 * how many tailored resumes Pro and the trial allow, and how long the trial
 * lasts. The Plan page every user sees shows these numbers.
 */
const PlansPage: React.FC = () => {
  const { show, toast } = useToast();
  const [settings, setSettings] = useState<PlanSettings | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await opsApi.planSettings();
      setSettings(loaded);
      setForm(formOf(loaded));
      setError(null);
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) setForbidden(true);
      else setError(errorMessage(e, 'Could not load the plan settings.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (name: keyof Form) => (event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => (current ? { ...current, [name]: event.target.value } : current));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setProblems({});
    try {
      const saved = await opsApi.savePlanSettings({
        pro_price_monthly: form.pro_price_monthly.trim() || null,
        pro_price_yearly: form.pro_price_yearly.trim() || null,
        pro_tailored_per_month: Number(form.pro_tailored_per_month),
        trial_days: Number(form.trial_days),
        trial_tailored: Number(form.trial_tailored),
      });
      setSettings(saved);
      setForm(formOf(saved));
      show('Saved. The Plan page shows the new numbers now.');
    } catch (e) {
      const fields = fieldErrors(e);
      if (Object.keys(fields).length) setProblems(fields);
      else show(errorMessage(e, 'The settings could not be saved.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (forbidden) return <Alert severity="info">Only the owner of this JobFlo installation can change the plans.</Alert>;

  const changed = settings && form ? JSON.stringify(form) !== JSON.stringify(formOf(settings)) : false;
  const monthly = Number(form?.pro_price_monthly);
  const yearly = Number(form?.pro_price_yearly);
  const saving_percent = monthly > 0 && yearly > 0 ? Math.max(0, Math.round((1 - yearly / (monthly * 12)) * 100)) : null;
  const dollars = { startAdornment: <InputAdornment position="start">$</InputAdornment> };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {!form ? (
        !error && <Skeleton variant="rounded" height={260} />
      ) : (
        <Stack spacing={2}>
          <Paper variant="outlined" component="section" aria-labelledby="plan-prices-title" sx={{ p: 2 }}>
            <Typography id="plan-prices-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              Pro: price
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In US dollars. Shown on every user’s Plan page. Leave a price empty to show none. Payments are not connected
              yet; when they are, a new price applies to new subscriptions and existing subscribers keep theirs until you
              decide otherwise.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
              <TextField label="Per month" value={form.pro_price_monthly} onChange={set('pro_price_monthly')} InputProps={dollars} inputProps={{ inputMode: 'decimal' }} error={!!problems.pro_price_monthly} helperText={problems.pro_price_monthly} sx={{ width: { sm: 200 } }} />
              <TextField
                label="Per year"
                value={form.pro_price_yearly}
                onChange={set('pro_price_yearly')}
                InputProps={dollars}
                inputProps={{ inputMode: 'decimal' }}
                error={!!problems.pro_price_yearly}
                helperText={problems.pro_price_yearly || (saving_percent !== null ? `${saving_percent}% less than twelve months at the monthly price` : ' ')}
                sx={{ width: { sm: 320 } }}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" component="section" aria-labelledby="plan-limits-title" sx={{ p: 2 }}>
            <Typography id="plan-limits-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              Allowances and the trial
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A changed allowance applies at once. A changed trial length applies to accounts made from now on. Free has no
              tailored resumes and no email tracking.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
              <TextField label="Pro: tailored resumes a month" value={form.pro_tailored_per_month} onChange={set('pro_tailored_per_month')} inputProps={{ inputMode: 'numeric' }} error={!!problems.pro_tailored_per_month} helperText={problems.pro_tailored_per_month} />
              <TextField label="Trial: days" value={form.trial_days} onChange={set('trial_days')} inputProps={{ inputMode: 'numeric' }} error={!!problems.trial_days} helperText={problems.trial_days || '0 means no trial'} />
              <TextField label="Trial: tailored resumes" value={form.trial_tailored} onChange={set('trial_tailored')} inputProps={{ inputMode: 'numeric' }} error={!!problems.trial_tailored} helperText={problems.trial_tailored} />
            </Stack>
          </Paper>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button variant="contained" onClick={save} disabled={saving || !changed}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {settings?.updated_at ? `Last changed ${formatDateTime(settings.updated_at)}${settings.updated_by ? ` by ${settings.updated_by}` : ''}` : 'Never changed: these are the starting values.'}
            </Typography>
          </Stack>
        </Stack>
      )}
      {toast}
    </Box>
  );
};

export default PlansPage;
